// form
import { Controller, useFormContext } from "react-hook-form";
// @mui
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormGroup,
  FormHelperText,
} from "@mui/material";

// ----------------------------------------------------------------------

interface RHFCheckboxProps extends Omit<FormControlLabelProps, "control"> {
  name: string;
}

export function RHFCheckbox({ name, ...other }: RHFCheckboxProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl error={!!fieldState?.error}>
          <FormControlLabel
            control={<Checkbox {...field} checked={field.value} />}
            {...other}
          />
          {fieldState?.error?.message && (
            <FormHelperText>
              {fieldState.error.message as string}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

// ----------------------------------------------------------------------

interface RHFMultiCheckboxProps
  extends Omit<FormControlLabelProps, "control" | "label"> {
  name: string;
  options: {
    label: string;
    value: any;
  }[];
  rules?: any;
}

export function RHFMultiCheckbox({
  name,
  options,
  rules,
  ...other
}: RHFMultiCheckboxProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const onSelected = (option: string) =>
          field.value.includes(option)
            ? field.value.filter((value: string) => value !== option)
            : [...field.value, option];

        return (
          <FormControl component="fieldset" error={!!fieldState?.error}>
            <FormGroup>
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={field.value.includes(option.value)}
                      onChange={() => field.onChange(onSelected(option.value))}
                    />
                  }
                  label={option.label}
                  {...other}
                />
              ))}
            </FormGroup>
            {fieldState?.error?.message && (
              <FormHelperText>
                {fieldState.error.message as string}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
}
